import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IProductDataV3 } from '@df/catalog/product/product';
import { GtmService } from '@df/module-gtm/gtm.service';
import { UiComponent } from '@df/ui/ui.component';
import { filter } from 'rxjs/operators';
import { BasketKlarnaContentBlockComponent } from '../../../../../basket/basket-klarna-content-block.component';
import { ExponeaStockNotificationComponent } from '../../../../../module/exponea/exponea-stock-notification.component';
import { PageSidebarComponent } from '../../../../../ui/display/page-sidebar/page-sidebar.component';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { ProductReviewsComponent } from '../../../review/product-reviews.component';
import { ProductDeliveryPriceComponent } from '../../delivery/price/product-delivery-price.component';
import { ProductLayoutDetailsGeneralDimensionsComponent } from '../details/general/dimensions/product-layout-details-general-dimensions.component';
import { ProductLayoutDetailsGeneralComponent } from '../details/general/product-layout-details-general.component';
import { ProductLayoutGiftBoxingComponent } from '../gift/boxing/product-layout-gift-boxing.component';
import { ProductLayoutComponent } from '../product-layout.component';
import { NProductLayout } from '../product-layout.interface';
import { ProductLayoutSidebarHowToMeasureComponent } from './how-to-measure/product-layout-sidebar-how-to-measure.component';
import { ProductLayoutSidebarSizeGuideComponent } from './size-guide/product-layout-sidebar-size-guide.component';

export type TSidebar =
    | 'details'
    | 'reviews'
    | 'klarna'
    | 'gift-boxing'
    | 'delivery'
    | 'size-guide'
    | 'stock-notification'
    | 'how-to-measure'
    | 'dimensions';

export interface IProductSidebar {
    title: string;
    sidebar: TSidebar;
}

@Component({
    selector: 'product-layout-sidebar',
    templateUrl: './product-layout-sidebar.component.html',
    imports: [
        BasketKlarnaContentBlockComponent,
        ExponeaStockNotificationComponent,
        PageSidebarComponent,
        ProductDeliveryPriceComponent,
        ProductLayoutDetailsGeneralComponent,
        ProductLayoutDetailsGeneralDimensionsComponent,
        ProductLayoutGiftBoxingComponent,
        ProductLayoutSidebarHowToMeasureComponent,
        ProductLayoutSidebarSizeGuideComponent,
        ProductReviewsComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutSidebarComponent extends UiComponent implements OnInit {
    readonly product = input.required<ProductDecorator<IProductDataV3>>();
    readonly content = input.required<NProductLayout.IContent>();

    readonly productViewLayout = inject(ProductLayoutComponent);
    private readonly router = inject(Router);
    private readonly gtmService = inject(GtmService);

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
                this.productViewLayout.isSidebarOpen = false;
                this.detectChanges();
            })
        );

        this.subscriptions.add(this.productViewLayout.detectChanges$.subscribe(() => this.detectChanges()));
    }

    handleIsOpenChange(): void {
        this.productViewLayout.detectChanges$.next();
    }

    handleCloseRequest(): void {
        if (this.productViewLayout.sidebar === 'how-to-measure') {
            this.productViewLayout.sidebar = 'size-guide';
            this.detectChanges();
        } else {
            this.productViewLayout.isSidebarOpen = false;

            const sidebar = this.productViewLayout.sidebar;

            if (sidebar) {
                this.sendGtmEvent('close' + sidebar.charAt(0).toUpperCase() + sidebar.slice(1));
            }
        }
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }
}
